import React, { useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import MaskedInput from 'react-text-mask';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import * as helpers from '~/Utils/Helpers';
import { toPascalCaseWithSpaces } from '~/Pages/Vendas/helpers';
import history from '~/Config/history';

// Defaults históricos da tela de Cadastro Rápido - Lançamento (W-???).
// Quem precisar de outro empreendimento (ex.: DW-0998 check-in SetGarden 11384)
// passa as props codigoFamilia/codigoCorretor/codigoGerente.
const CODIGO_FAMILIA_DEFAULT = 11426;
const CODIGO_CORRETOR_DEFAULT = 1078;
const CODIGO_GERENTE_DEFAULT = 1077;

const NOMES_EMPREENDIMENTO = {
  11426: '11426 - SETPARQUE WORKFIRE II',
  11384: '11384 - SETGARDEN',
};

const touchStyle = {
  minHeight: 44,
  fontSize: '1rem',
};

const validationSchema = Yup.object().shape({
  nome: Yup.string()
    .required('Informe o nome')
    .matches(/^[^\d]*$/, 'O nome não pode conter números'),
  tel_celular: Yup.string()
    .required('Informe o celular')
    .matches(
      /^(?:\(\d{2}\)\s?)?9\d{4}-\d{4}$/,
      'Número de celular inválido'
    ),
});

const ModalAdicionarProspectoRapido = ({
  show,
  onHide,
  onProspectoCriado,
  codigoFamilia = CODIGO_FAMILIA_DEFAULT,
  codigoCorretor = CODIGO_CORRETOR_DEFAULT,
  codigoGerente = CODIGO_GERENTE_DEFAULT,
  titulo = 'Novo Prospecto - Cadastro Rápido',
}) => {
  const [salvando, setSalvando] = useState(false);
  const [duplicata, setDuplicata] = useState(null);
  const [verificando, setVerificando] = useState(false);

  const verificarTelefone = async (telefone) => {
    const apenasNumeros = (telefone || '').replace(/\D/g, '');
    if (apenasNumeros.length !== 11) {
      setDuplicata(null);
      return;
    }

    setVerificando(true);
    try {
      const response = await api.consultaTelefone(apenasNumeros);
      if (response && response.codigo) {
        setDuplicata(response);
      } else {
        setDuplicata(null);
      }
    } catch (err) {
      setDuplicata(null);
    } finally {
      setVerificando(false);
    }
  };

  const salvar = async (values) => {
    setSalvando(true);
    try {
      const request = {
        nome: values.nome,
        tipo_pessoa: 0,
        tel_celular: values.tel_celular.replace(/\D/g, ''),
        tel_comercial: null,
        tel_residencial: null,
        email: null,
        cpf: null,
        codigo_nacionalidade: 1,
        codigo_tipo: 1,
        codigo_origem_cadastro: 1,
        prospecto_empreendimento: [
          {
            codigo_familia: codigoFamilia,
            codigo_corretor: codigoCorretor,
            codigo_gerente: codigoGerente,
            senha: 0,
          },
        ],
      };

      const response = await api.criarProspecto(request);
      if (response && response.codigo) {
        toast.success('Prospecto criado com sucesso');
        if (onProspectoCriado) {
          onProspectoCriado(response.codigo);
        }
      }
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao criar prospecto';
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  const fechar = () => {
    setDuplicata(null);
    setVerificando(false);
    onHide();
  };

  const redirecionarParaGestao = () => {
    if (duplicata && duplicata.codigo) {
      fechar();
      history.push('/prospecto/gestao-crm/' + duplicata.codigo);
    }
  };

  return (
    <>
      <Modal backdrop="static" show={show && !duplicata} onHide={fechar}>
        <Modal.Header closeButton>
          <Modal.Title>{titulo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{ nome: '', tel_celular: '' }}
            validationSchema={validationSchema}
            onSubmit={salvar}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              setFieldValue,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="empreendimento">
                  <Form.Label>Empreendimento</Form.Label>
                  <Form.Control
                    as="select"
                    disabled
                    value={codigoFamilia}
                    style={touchStyle}
                  >
                    <option value={codigoFamilia}>
                      {NOMES_EMPREENDIMENTO[codigoFamilia] || codigoFamilia}
                    </option>
                  </Form.Control>
                </Form.Group>

                <Form.Group controlId="nome">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome"
                    maxLength="100"
                    onChange={(e) =>
                      setFieldValue(
                        'nome',
                        toPascalCaseWithSpaces(e.target.value)
                      )
                    }
                    onBlur={handleBlur}
                    value={values.nome}
                    isInvalid={errors.nome && touched.nome}
                    placeholder="Nome do cliente"
                    disabled={salvando}
                    style={touchStyle}
                    autoFocus
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nome}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="tel_celular">
                  <Form.Label>Celular</Form.Label>
                  <MaskedInput
                    type="tel"
                    className={
                      'form-control' +
                      (errors.tel_celular && touched.tel_celular
                        ? ' is-invalid'
                        : '')
                    }
                    name="tel_celular"
                    onChange={(e) => {
                      setFieldValue('tel_celular', e.target.value);
                      verificarTelefone(e.target.value);
                    }}
                    onBlur={handleBlur}
                    value={values.tel_celular}
                    mask={helpers.mask.celular}
                    placeholder="(00) 00000-0000"
                    disabled={salvando || verificando}
                    style={touchStyle}
                  />
                  {errors.tel_celular && touched.tel_celular && (
                    <small className="form-text text-danger">
                      {errors.tel_celular}
                    </small>
                  )}
                </Form.Group>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={fechar}
                    disabled={salvando}
                    style={{ ...touchStyle, padding: '0.5rem 1.5rem' }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={salvando || verificando}
                    style={{ ...touchStyle, padding: '0.5rem 1.5rem', fontWeight: 600 }}
                  >
                    {salvando ? (
                      <span>
                        <FaSpinner className="fa-spin" /> Salvando...
                      </span>
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      <Modal
        show={!!duplicata}
        onHide={() => setDuplicata(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Prospecto já existente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Esse telefone já está sendo utilizado por
          {duplicata && (
            <dl className="mt-2 mb-0">
              <dt>Código</dt>
              <dd>{duplicata.codigo}</dd>
              <dt>Nome</dt>
              <dd>{duplicata.nome}</dd>
              <dt>Cadastrado em</dt>
              <dd>{helpers.format.date(duplicata.data_cadastro)}</dd>
            </dl>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => setDuplicata(null)}
            style={touchStyle}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={redirecionarParaGestao}
            style={{ ...touchStyle, fontWeight: 600 }}
          >
            Ir para Gestão do Prospecto
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalAdicionarProspectoRapido;
